import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Button,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { parseLocaleDecimal } from '../../../../utils/localeNumber';
import { DecimalNumberInput } from '../../../../components/ui';
import { PaymentEntry } from '../../../../types/sales.types';
import { PAYMENT_METHODS } from '../../../../utils/constants/paymentMethods';
import SalesStepHint from '../SalesStepHint/SalesStepHint';
import { salesNestedDialogSx } from '../salesModalStack';
import { useTabScopedOpen } from '../../../dashboard/TabPanelActiveContext';

const ModalCancelButton = styled(Button)(({ theme }) => ({
  boxShadow: 'none',
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  border: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: 'none',
  },
}));

const ModalActionButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: '#ffffff',
  boxShadow: 'none',
  '&:hover': {
    background: 'linear-gradient(to right, #0891b2, #059669)',
    boxShadow: 'none',
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    background: theme.palette.action.disabledBackground,
  },
}));

export type PaymentAmountSubmitHandler = (
  method: string,
  amount: number,
  cashReceived?: number,
  notes?: string,
  bankReference?: string,
  qrTransactionId?: string,
) => void;

export type PaymentAmountUpdateHandler = (
  paymentId: string,
  amount: number,
  cashReceived?: number,
  notes?: string,
  bankReference?: string,
  qrTransactionId?: string,
) => void;

export interface PaymentAmountDialogProps {
  open: boolean;
  onClose: () => void;
  method: string | null;
  editingPayment: PaymentEntry | null;
  remainingBalance: number;
  onAdd: PaymentAmountSubmitHandler;
  onUpdate: PaymentAmountUpdateHandler;
  formatCurrency: (amount: number) => string;
  /** When true (default), new payments start with the remaining balance filled in. */
  prefillRemainingAmount?: boolean;
}

/**
 * Shared add/edit payment amount dialog for nueva venta, intercambio and procesar pago.
 * Cash: single amount field; if greater than remaining, applies remaining and shows change.
 * Card/QR keep their reference fields.
 */
const PaymentAmountDialog: React.FC<PaymentAmountDialogProps> = ({
  open,
  onClose,
  method,
  editingPayment,
  remainingBalance,
  onAdd,
  onUpdate,
  formatCurrency,
  prefillRemainingAmount = true,
}) => {
  const scopedOpen = useTabScopedOpen(open);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [bankReference, setBankReference] = useState('');
  const [qrTransactionId, setQrTransactionId] = useState('');
  const isCash = method === PAYMENT_METHODS.CASH;
  const isCard = method === PAYMENT_METHODS.CARD;
  const isQr = method === PAYMENT_METHODS.QR;
  const isEditing = editingPayment != null;
  const effectiveRemaining =
    isEditing && editingPayment != null
      ? remainingBalance + editingPayment.amount
      : remainingBalance;
  const parsedAmount = parseLocaleDecimal(amount) ?? 0;
  const hasCashChange = isCash && parsedAmount > effectiveRemaining;
  const changeAmount = hasCashChange ? parsedAmount - effectiveRemaining : 0;
  const appliedAmount = isCash ? Math.min(parsedAmount, effectiveRemaining) : parsedAmount;

  useEffect(() => {
    if (!open) return;
    if (isEditing && editingPayment) {
      const existingNotes = editingPayment.paymentNotes ?? '';
      const displayAmount = editingPayment.cashReceived ?? editingPayment.amount;
      setAmount(displayAmount.toString());
      setNotes(existingNotes);
      setNotesExpanded(existingNotes.trim() !== '');
      setBankReference(editingPayment.bankReference ?? '');
      setQrTransactionId(editingPayment.qrTransactionId ?? '');
    } else {
      setAmount(
        prefillRemainingAmount && effectiveRemaining > 0 ? effectiveRemaining.toString() : '',
      );
      setNotes('');
      setNotesExpanded(false);
      setBankReference('');
      setQrTransactionId('');
    }
    // Only re-prefill when the dialog opens or the edited payment changes —
    // not when remainingBalance drifts mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, editingPayment, open, prefillRemainingAmount]);

  const handleSubmit = () => {
    const numAmount = parseLocaleDecimal(amount) ?? 0;
    if (numAmount <= 0 || method == null) return;

    if (!isCash && numAmount > effectiveRemaining) return;

    const paymentAmount = isCash ? Math.min(numAmount, effectiveRemaining) : numAmount;
    if (paymentAmount <= 0) return;

    // Persist cashReceived only when tender exceeds the applied amount (true change).
    const cashReceivedValue = isCash && numAmount > paymentAmount ? numAmount : undefined;
    const notesValue = notes.trim() !== '' ? notes.trim() : undefined;
    const bankReferenceValue =
      isCard && bankReference.trim() !== '' ? bankReference.trim() : undefined;
    const qrTransactionIdValue =
      isQr && qrTransactionId.trim() !== '' ? qrTransactionId.trim() : undefined;

    if (isEditing && editingPayment) {
      onUpdate(
        editingPayment.id,
        paymentAmount,
        cashReceivedValue,
        notesValue,
        bankReferenceValue,
        qrTransactionIdValue,
      );
    } else {
      onAdd(
        method,
        paymentAmount,
        cashReceivedValue,
        notesValue,
        bankReferenceValue,
        qrTransactionIdValue,
      );
    }
    onClose();
  };

  return (
    <Dialog open={scopedOpen} onClose={onClose} maxWidth="sm" fullWidth sx={salesNestedDialogSx}>
      <DialogTitle>{isEditing ? 'Editar Pago' : 'Agregar Pago'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {method == null && !isEditing && (
            <SalesStepHint tone="success">Seleccione un método de pago.</SalesStepHint>
          )}
          <DecimalNumberInput
            label="Monto del pago"
            value={amount}
            onChange={(val) => setAmount(val != null ? String(val) : '')}
            decimalPlaces={2}
            min={0.01}
            max={999999.99}
            fullWidth
            sx={{ mb: 2 }}
            helperText={
              isCash
                ? `Saldo pendiente: ${formatCurrency(effectiveRemaining)}`
                : `Máximo: ${formatCurrency(effectiveRemaining)}`
            }
            error={!isCash && parsedAmount > effectiveRemaining}
            disabled={method == null && !isEditing}
            InputProps={{
              startAdornment: <InputAdornment position="start">Bs</InputAdornment>,
            }}
          />
          {isCard && (
            <TextField
              label="Referencia de Tarjeta"
              value={bankReference}
              onChange={(e) => setBankReference(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          {isQr && (
            <TextField
              label="ID de Transacción QR"
              value={qrTransactionId}
              onChange={(e) => setQrTransactionId(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: notesExpanded ? 0 : 1 }}>
            <Tooltip title="Notas">
              <IconButton
                size="small"
                aria-label={notesExpanded ? 'Ocultar notas' : 'Mostrar notas'}
                aria-expanded={notesExpanded}
                onClick={() => setNotesExpanded((prev) => !prev)}
                sx={{ color: 'text.secondary' }}
              >
                {notesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          <Collapse in={notesExpanded} timeout={200} unmountOnExit>
            <TextField
              label="Notas (opcional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
          </Collapse>
          {hasCashChange && (
            <SalesStepHint tone="success">
              Se aplicará {formatCurrency(appliedAmount)}. Cambio a entregar:{' '}
              {formatCurrency(changeAmount)}
            </SalesStepHint>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <ModalCancelButton variant="tonal" onClick={onClose}>
          Cancelar
        </ModalCancelButton>
        <ModalActionButton
          onClick={handleSubmit}
          variant="contained"
          disabled={
            method == null ||
            amount === '' ||
            parsedAmount <= 0 ||
            (!isCash && parsedAmount > effectiveRemaining) ||
            (isCash && appliedAmount <= 0)
          }
        >
          {isEditing ? 'Guardar' : 'Agregar'}
        </ModalActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentAmountDialog;
